import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results", "resultsList"];
  timeout = null;
  currentSearchId = 0;
  selectedIndex = 0;

  connect() {
    this._bindClickOutsideTopSearchBarToCollapseSearchBar();
    this._bindEscapeKeyInSearchFieldToCollapseSearchBar();
    this._bindArrowKeysForNavigation();
  }

  update(event) {
    event.preventDefault();
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      const searchId = ++this.currentSearchId;
      const query = this.inputTarget.value;

      this.search(query, searchId);
    }, 200);
  }

  disconnect() {
    const hideMobileElements = document.querySelectorAll('.hide-on-mobile-while-searching');
    const searchResults = document.getElementById('search-results');
    const mainResults = document.getElementById('main-results');

    searchResults.classList.remove('block');
    mainResults.classList.remove('block');
    mainResults.classList.add('hidden');
    hideMobileElements.forEach(element => element.classList.remove('max-sm:hidden'));

    this.collapseSearchBar();
    this.hideSearchResults();
    this.inputTarget.value = '';

    this._ubindClickOutsideTopSearchBarToCollapseSearchBar()
  }

  search(query, searchId) {
    const url = new URL("/search", window.location.origin);
    url.search = new URLSearchParams({ search: query, format: "turbo_stream" }).toString();

    this.showSkeletonResults();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch(url, {
      signal: controller.signal,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.text();
      })
      .then((html) => {
        clearTimeout(timeoutId);
        if (searchId === this.currentSearchId) {
          Turbo.renderStreamMessage(html);
          requestAnimationFrame(() => {
            this.showSearchResults();
            this.selectedIndex = 0;
          });
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        if (searchId === this.currentSearchId && error.name !== "AbortError") {
          console.error("Search error:", error);
        }
        if (searchId === this.currentSearchId) {
          this.showEmptyResults();
        }
      });
  }

  navigate(event) {
    if (event.preventDefault) {
      event.preventDefault();
    }
    const url = event.currentTarget.getAttribute('href');
    this.collapseSearchBar();
    Turbo.visit(url);
  }

  showSkeletonResults() {
    const skeleton = document.getElementById('search-results-skeleton');
    const frame = document.getElementById('search-results-frame');
    if (frame.innerHTML === skeleton.innerHTML) return;

    frame.innerHTML = skeleton.innerHTML;
    this.showSearchResults();
  }

  showEmptyResults() {
    const emptyTemplate = document.getElementById('search-results-empty');
    const frame = document.getElementById('search-results-frame');
    if (emptyTemplate?.content && frame) {
      frame.replaceChildren(emptyTemplate.content.cloneNode(true));
      this.showSearchResults();
    }
  }

  showSearchResults() {
    const searchResults = document.getElementById('search-results');
    if (!searchResults) return;
    searchResults.classList.add('block');
    searchResults.classList.remove('hidden');
  }

  hideSearchResults() {
    const searchResults = document.getElementById('search-results');
    searchResults.classList.remove('block');
    searchResults.classList.add('hidden');
  }

  expandSearchBar() {
    const searchField = this.element.querySelector('.top-bar-search__search-field');
    const hideMobileElements = document.querySelectorAll('.hide-on-mobile-while-searching');
    const mainResults = document.getElementById('main-results');

    hideMobileElements.forEach(element => {
      element.classList.add('max-sm:hidden');
    });

    mainResults.classList.remove('hidden');
    mainResults.classList.add('block');

    this.element.classList.add('top-bar-search--expanded');

    searchField.focus();

    if (this.inputTarget.value && this.inputTarget.value.length > 0) {
      this.showSearchResults();
    }
  }

  collapseSearchBar(_event) {
    const hideMobileElements = document.querySelectorAll('.hide-on-mobile-while-searching');
    const mainResults = document.getElementById('main-results');

    this.element.classList.remove('top-bar-search--expanded');

    hideMobileElements.forEach(element => {
      element.classList.remove('max-sm:hidden');
    });

    mainResults.classList.remove('block');
    mainResults.classList.add('hidden');

    this.hideSearchResults();
  }

  _bindEscapeKeyInSearchFieldToCollapseSearchBar() {
    this.element.querySelector('.top-bar-search__search-field').addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.collapseSearchBar();
      }
    });
  }

  _collapseSearchBarHandler(event) {
    const topBarSearch = document.getElementById('top-bar-search');
    if (!topBarSearch || !event.target) return;
    if (!topBarSearch.contains(event.target)) {
      this.collapseSearchBar();
    }
  }

  _bindClickOutsideTopSearchBarToCollapseSearchBar() {
    this._boundCollapseSearchBarHandler = this._collapseSearchBarHandler.bind(this);
    document.addEventListener("mousedown", this._boundCollapseSearchBarHandler);
  }

  _ubindClickOutsideTopSearchBarToCollapseSearchBar() {
    if (this._boundCollapseSearchBarHandler) {
      document.removeEventListener("mousedown", this._boundCollapseSearchBarHandler);
      this._boundCollapseSearchBarHandler = null;
    }
  }

  _bindArrowKeysForNavigation() {
    this.inputTarget.addEventListener("keydown", (event) => {
      if (!this.hasResultsListTarget) return;

      const results = this.resultsListTarget.querySelectorAll('a[data-selected]');
      if (!results.length) return;

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          this._moveSelection(1, results);
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          this._moveSelection(-1, results);
          break;
        }
        case "Enter": {
          event.preventDefault();
          const selectedResult = this.resultsListTarget.querySelector('a[data-selected="true"]');
          if (selectedResult) {
            this.navigate({ currentTarget: selectedResult });
          }
          break;
        }
      }
    });
  }

  _moveSelection(direction, results) {
    const currentSelected = this.resultsListTarget.querySelector('a[data-selected="true"]');
    const currentIndex = Array.from(results).indexOf(currentSelected);
    const newIndex = Math.max(0, Math.min(results.length - 1, currentIndex + direction));

    if (currentSelected) {
      currentSelected.dataset.selected = "false";
    }

    const newSelected = results[newIndex];
    newSelected.dataset.selected = "true";
    this.selectedIndex = newIndex;
  }
}
